import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney } from "@/lib/api";
import { Send, Plus, ArrowUpRight } from "lucide-react";

export default function CustomerDashboard() {
  const [wallet, setWallet] = useState(null);
  const [txns, setTxns] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [w, t] = await Promise.all([api.get("/wallet"), api.get("/transactions")]);
        setWallet(w.data);
        setTxns(t.data.slice(0, 8));
      } catch {}
    })();
  }, []);

  const balances = wallet?.balances || {};
  const nonZero = Object.entries(balances).filter(([, v]) => v > 0);
  const primary = nonZero[0] || ["usd", balances.usd ?? 0];

  return (
    <main data-testid="customer-dashboard" className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="grid grid-cols-12 gap-8 items-end mb-12">
        <div className="col-span-12 lg:col-span-8">
          <div className="text-xs uppercase tracking-[0.3em] text-gold">Patron Vault</div>
          <h1 className="font-display text-5xl tracking-tighter mt-3">Available to move</h1>
          <div data-testid="wallet-balance-headline" className="font-display text-7xl lg:text-9xl gold-text mt-4 tracking-tighter font-light">
            {formatMoney(primary[1], primary[0])}
          </div>
          {nonZero.length > 1 && (
            <div className="mt-4 flex flex-wrap gap-3">
              {nonZero.slice(1).map(([c, v]) => (
                <div key={c} data-testid={`balance-${c}`} className="text-sm">
                  <span className="text-muted-fg uppercase tracking-widest text-xs mr-2">{c}</span>
                  <span className="font-mono text-gold">{formatMoney(v, c)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="col-span-12 lg:col-span-4 flex flex-col gap-3">
          <Link to="/send" data-testid="cta-send" className="btn-gold inline-flex items-center justify-center gap-2">
            <Send size={16} /> Send money
          </Link>
          <Link to="/wallet" data-testid="cta-topup" className="btn-ghost inline-flex items-center justify-center gap-2">
            <Plus size={16} /> Top up
          </Link>
        </div>
      </div>

      <div className="gold-underline mb-12" />

      <div className="grid grid-cols-12 gap-6">
        <QuickAction to="/send" title="Send" subtitle="To anyone with a y ℶα email" testid="action-send" />
        <QuickAction to="/wallet" title="Top up" subtitle="From any card · 6 currencies" testid="action-topup" />
        <QuickAction to="/transactions" title="Ledger" subtitle="Every movement, archived" testid="action-ledger" />
      </div>

      <section className="mt-16">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display text-3xl tracking-tight">Recent movements</h2>
          <Link to="/transactions" className="text-sm text-gold hover:text-white transition-colors flex items-center gap-1">
            View all <ArrowUpRight size={14} />
          </Link>
        </div>
        <TxList items={txns} />
      </section>
    </main>
  );
}

function QuickAction({ to, title, subtitle, testid }) {
  return (
    <Link to={to} data-testid={testid} className="col-span-12 md:col-span-4 lux-card p-8 group">
      <div className="font-display text-3xl">{title}</div>
      <div className="text-sm text-secondary-fg mt-2">{subtitle}</div>
      <div className="mt-6 text-gold flex items-center gap-2 text-sm">
        Continue <ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
      </div>
    </Link>
  );
}

function TxList({ items }) {
  if (!items?.length) {
    return (
      <div className="border hairline p-12 text-center">
        <div className="font-display text-2xl text-secondary-fg">No movements yet</div>
      </div>
    );
  }
  return (
    <div className="border hairline">
      {items.map((t) => (
        <div key={t.tx_id} data-testid={`tx-row-${t.tx_id}`} className="grid grid-cols-12 gap-4 px-6 py-5 border-b hairline last:border-b-0 hover:bg-surface transition-colors">
          <div className="col-span-7">
            <div className="text-sm">{t.description || t.type}</div>
            <div className="text-xs text-muted-fg mt-1">{new Date(t.created_at).toLocaleString()}</div>
          </div>
          <div className="col-span-2 text-xs uppercase tracking-widest text-muted-fg self-center">{t.type?.replace(/_/g, " ")}</div>
          <div className={`col-span-3 text-right font-display text-xl ${t.direction === "in" ? "text-gold" : "text-secondary-fg"}`}>
            {t.direction === "in" ? "+" : "−"} {formatMoney(t.amount, t.currency)}
          </div>
        </div>
      ))}
    </div>
  );
}
